import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadPdf } from "@/lib/pdf";
import {
  Boxes, Search, Plus, Minus, AlertTriangle, Edit3, X, Save,
  TrendingUp, Package, BookOpen, Trash2, Download, Wrench, FileText
} from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

// Catégories cohérentes avec backend
const ARTICLE_CATEGORIES = [
  { value: "general", label: "Général" },
  { value: "peinture", label: "Peinture" },
  { value: "outillage", label: "Outillage" },
  { value: "fixation", label: "Fixation" },
  { value: "consommable", label: "Consommable" },
  { value: "autre", label: "Autre" },
];

const MATERIAL_CATEGORIES = [
  { value: "peinture", label: "Peinture" },
  { value: "enduit", label: "Enduit" },
  { value: "colle", label: "Colle" },
  { value: "toile_papier", label: "Toile / Papier" },
  { value: "fixation", label: "Fixation" },
  { value: "outillage_consommable", label: "Outillage / Consommable" },
  { value: "protection", label: "Protection" },
  { value: "diluant", label: "Diluant" },
  { value: "primaire", label: "Primaire" },
  { value: "vernis", label: "Vernis" },
  { value: "joint", label: "Joint" },
  { value: "divers", label: "Divers" },
];

const UNITS = ["L", "kg", "g", "m²", "ml", "u", "pot", "sac", "ml"];

export default function Stock() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");        // all | low | ok
  const [typeFilter, setTypeFilter] = useState("all"); // all | article | materiau
  const [editor, setEditor] = useState(null);          // { item }
  const [creator, setCreator] = useState(null);        // null | "choose" | "article" | "materiau"

  const load = async () => {
    const { data } = await api.get("/stock");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-tertiary-text flex items-center gap-2"><span className="spinner" /> Chargement…</div>;

  const items = (data.items || []).filter((it) => {
    if (typeFilter !== "all" && it.type !== typeFilter) return false;
    if (filter === "low" && !it.is_low_stock) return false;
    if (filter === "ok" && it.is_low_stock) return false;
    if (!q) return true;
    const s = `${it.code || ""} ${it.description || ""} ${it.supplier || ""} ${it.category || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const counts = {
    all: data.items?.length || 0,
    article: (data.items || []).filter((i) => i.type === "article").length,
    materiau: (data.items || []).filter((i) => i.type === "materiau").length,
  };

  const adjust = async (id, delta) => {
    try {
      await api.patch(`/stock/${id}/adjust`, { delta });
      await load();
    } catch {
      toast.error("Erreur ajustement stock");
    }
  };

  const removeItem = async (it) => {
    const label = it.type === "article" ? "article" : "matériau";
    if (!window.confirm(`Supprimer définitivement ce ${label} « ${it.description} » ?\n\nCette action est irréversible.`)) return;
    try {
      const url = it.type === "article" ? `/work-items/${it.id}` : `/materials/${it.id}`;
      await api.delete(url);
      toast.success(`${label[0].toUpperCase() + label.slice(1)} supprimé`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const exportCsv = () => downloadPdf("/stock/export.csv");

  return (
    <div data-testid="stock-page" className="max-w-7xl mx-auto">
      <header className="mb-8 animate-in">
        <div className="flex items-center gap-2 text-xs font-semibold mb-3" style={{ color: "#FFC400" }}>
          <Boxes size={13} strokeWidth={2} /> Stock — Articles & Matériaux
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
              Stock <span className="font-display" style={{ color: "#FFC400" }}>unifié</span>
            </h1>
            <p className="text-secondary-text mt-3 max-w-3xl text-[15px]">
              Inventaire de vos <b>articles</b> (achat/revente avec marge) et de vos <b>matériaux</b> (consommables, prix d&apos;achat unique). Ajustez les quantités, définissez un seuil d&apos;alerte et basculez en mode &quot;à commander&quot; dans la fiche du devis.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCreator("choose")} className="btn btn-primary" data-testid="stock-add">
              <Plus size={14} /> Ajouter
            </button>
            <button onClick={exportCsv} className="btn btn-outline" data-testid="stock-export-csv" title="Export CSV pour le comptable">
              <Download size={14} /> Export comptable
            </button>
            <Link to="/library" className="btn btn-outline">
              <BookOpen size={14} /> Bibliothèque
            </Link>
          </div>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Kpi icon={Package} label="Éléments référencés" value={data.total_count} tone="muted" testid="stock-kpi-count" />
        <Kpi icon={AlertTriangle} label="En alerte de rupture" value={data.low_stock_count} tone={data.low_stock_count > 0 ? "danger" : "muted"} testid="stock-kpi-low" />
        <Kpi icon={TrendingUp} label="Valeur stock HT" value={fmtEUR(data.total_stock_value_ht)} tone="accent" testid="stock-kpi-value" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-text" />
          <input className="input pl-9 w-72" placeholder="Recherche…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="stock-search" />
        </div>
        {/* Type filter */}
        <div className="flex gap-1 p-1 rounded-lg border border-[var(--border)]" data-testid="stock-type-filter">
          {[
            { k: "all", label: `Tous (${counts.all})` },
            { k: "article", label: `Articles (${counts.article})` },
            { k: "materiau", label: `Matériaux (${counts.materiau})` },
          ].map(({ k, label }) => (
            <button
              key={k}
              onClick={() => setTypeFilter(k)}
              className={`btn btn-sm ${typeFilter === k ? "btn-accent" : "btn-ghost"}`}
              data-testid={`stock-type-${k}`}
            >{label}</button>
          ))}
        </div>
        {/* Status filter */}
        <div className="flex gap-1 p-1 rounded-lg border border-[var(--border)]" data-testid="stock-filter">
          {[
            { k: "all", label: "Tous statuts" },
            { k: "low", label: `Rupture (${data.low_stock_count})` },
            { k: "ok", label: "Stock OK" },
          ].map(({ k, label }) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`btn btn-sm ${filter === k ? "btn-primary" : "btn-ghost"}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="card text-center py-16" data-testid="stock-empty">
          <Boxes size={42} className="mx-auto mb-4 text-tertiary-text opacity-50" strokeWidth={1.5} />
          <div className="font-display text-xl mb-2" style={{ fontWeight: 600 }}>
            {data.total_count === 0 ? "Aucun article ni matériau enregistré" : "Aucun résultat"}
          </div>
          <div className="text-sm text-secondary-text max-w-md mx-auto">
            {data.total_count === 0 ? (
              <>
                Commencez en cliquant sur <button onClick={() => setCreator("choose")} className="underline" style={{ color: "#FFC400" }}>Ajouter</button> ci-dessus.
              </>
            ) : "Aucun élément ne correspond à ces filtres."}
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Code</th>
                <th>Nom</th>
                <th>Fournisseur</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Seuil</th>
                <th className="text-right">PA HT</th>
                <th className="text-right">PV HT</th>
                <th className="text-right">Marge</th>
                <th className="text-right">Valeur stock</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={`${it.type}-${it.id}`} data-testid={`stock-row-${it.id}`}>
                  <td>
                    <TypeBadge type={it.type} />
                  </td>
                  <td className="text-xs font-mono" style={{ color: "#FFC400" }}>{it.code || "—"}</td>
                  <td>
                    <div className="font-medium flex items-center gap-2">
                      {it.is_low_stock && (
                        <span className="badge badge-danger" title="Stock sous le seuil" data-testid={`stock-low-${it.id}`}>
                          <AlertTriangle size={11} /> Rupture
                        </span>
                      )}
                      {it.description}
                    </div>
                    {it.category && (
                      <div className="text-[11px] text-[var(--grey)] mt-0.5">{it.category}</div>
                    )}
                  </td>
                  <td className="text-[var(--ink-soft)] text-sm">{it.supplier || "—"}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => adjust(it.id, -1)} className="w-7 h-7 rounded border border-[var(--border)] hover:border-[#E10600] hover:text-[#E10600] inline-flex items-center justify-center transition-colors" data-testid={`stock-minus-${it.id}`} title="Retirer 1">
                        <Minus size={12} />
                      </button>
                      <span className="numerals min-w-[40px] text-center font-semibold" style={{ color: it.is_low_stock ? "#E10600" : "var(--chalk)" }}>
                        {Number(it.stock_qty || 0).toFixed((it.stock_qty % 1) ? 2 : 0)}
                      </span>
                      <button onClick={() => adjust(it.id, 1)} className="w-7 h-7 rounded border border-[var(--border)] hover:border-[#FFC400] hover:text-[#FFC400] inline-flex items-center justify-center transition-colors" data-testid={`stock-plus-${it.id}`} title="Ajouter 1">
                        <Plus size={12} />
                      </button>
                      <span className="text-[11px] text-[var(--grey)] ml-1">{it.unit}</span>
                    </div>
                  </td>
                  <td className="text-right text-[var(--grey)]">{Number(it.stock_threshold || 0).toFixed(0)}</td>
                  <td className="text-right">{fmtEUR(it.purchase_price_ht)}</td>
                  <td className="text-right font-semibold">{fmtEUR(it.sell_price_ht)}</td>
                  <td className="text-right" style={{ color: it.type === "article" ? "#FFC400" : "var(--grey)" }}>
                    {it.type === "article" ? (
                      <>
                        {fmtEUR(it.margin_amount)}
                        {it.effective_margin_pct != null && (
                          <span className="text-[11px] text-[var(--grey)] ml-1">({it.effective_margin_pct}%)</span>
                        )}
                      </>
                    ) : "—"}
                  </td>
                  <td className="text-right">{fmtEUR(it.stock_value_ht)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditor({ item: it })}
                        className="w-8 h-8 rounded-md border border-[var(--border)] hover:border-[#FFC400] hover:text-[#FFC400] inline-flex items-center justify-center transition-colors"
                        title="Modifier"
                        data-testid={`stock-edit-${it.id}`}
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        onClick={() => removeItem(it)}
                        className="w-8 h-8 rounded-md border border-[var(--border)] hover:border-[#E10600] hover:text-[#E10600] inline-flex items-center justify-center transition-colors"
                        title="Supprimer"
                        data-testid={`stock-delete-${it.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editeur */}
      {editor && (
        <StockEditor
          item={editor.item}
          onClose={() => setEditor(null)}
          onSaved={async () => { await load(); setEditor(null); }}
        />
      )}

      {/* Sélecteur type à la création */}
      {creator === "choose" && (
        <TypeChooserModal
          onChoose={(t) => setCreator(t)}
          onClose={() => setCreator(null)}
        />
      )}
      {(creator === "article" || creator === "materiau") && (
        <StockCreator
          kind={creator}
          onClose={() => setCreator(null)}
          onSaved={async () => { await load(); setCreator(null); }}
        />
      )}
    </div>
  );
}

// ── Badge type ──────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const isArt = type === "article";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
      style={{
        background: isArt ? "rgba(255,196,0,0.15)" : "rgba(225,6,0,0.12)",
        color: isArt ? "#FFC400" : "#E10600",
        border: `1px solid ${isArt ? "rgba(255,196,0,0.45)" : "rgba(225,6,0,0.40)"}`,
      }}
      data-testid={`type-badge-${type}`}
    >
      {isArt ? <Boxes size={9} /> : <Wrench size={9} />}
      {isArt ? "Article" : "Matériau"}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, tone = "muted", testid }) {
  const tones = {
    muted:  { value: "var(--chalk)" },
    accent: { value: "#FFC400" },
    danger: { value: "#E10600" },
  }[tone];
  return (
    <div className={`kpi ${tone === "danger" ? "danger" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="kpi-label">{label}</div>
        <Icon size={16} className="text-tertiary-text shrink-0" strokeWidth={1.6} />
      </div>
      <div className="kpi-value" style={{ color: tones.value }}>{value}</div>
    </div>
  );
}

// ── Modal de choix Matériau / Article ───────────────────────────────────
function TypeChooserModal({ onChoose, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="type-chooser-modal">
      <div className="card max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--border)]">
          <div>
            <div className="text-xs font-semibold" style={{ color: "#FFC400" }}>Nouvel élément stock</div>
            <h3 className="font-display text-xl mt-0.5">Que voulez-vous ajouter ?</h3>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onChoose("article")}
            className="card hover:border-[#FFC400] p-5 text-left transition-all hover:-translate-y-0.5"
            data-testid="type-choose-article"
          >
            <div className="flex items-center gap-2 mb-2">
              <Boxes size={20} style={{ color: "#FFC400" }} />
              <span className="font-display text-lg">Article</span>
            </div>
            <p className="text-[12px] text-[var(--ink-soft)] leading-relaxed">
              Produit d&apos;<b>achat/revente</b> avec marge (code, prix d&apos;achat, prix de vente, stock géré). Apparaît dans les devis avec marge.
            </p>
          </button>
          <button
            onClick={() => onChoose("materiau")}
            className="card hover:border-[#E10600] p-5 text-left transition-all hover:-translate-y-0.5"
            data-testid="type-choose-materiau"
          >
            <div className="flex items-center gap-2 mb-2">
              <Wrench size={20} style={{ color: "#E10600" }} />
              <span className="font-display text-lg">Matériau</span>
            </div>
            <p className="text-[12px] text-[var(--ink-soft)] leading-relaxed">
              <b>Consommable</b> facturé au prix d&apos;achat (peinture, enduit, colle, vis…). Utilisé dans les ouvrages OU au coup par coup dans les devis.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Création unifiée Article OU Matériau ────────────────────────────────
function StockCreator({ kind, onClose, onSaved }) {
  const isArt = kind === "article";
  const [form, setForm] = useState(() => isArt ? ({
    code: "", description: "", category: "general", unit: "u",
    supplier: "", purchase_price_ht: 0, margin_pct: 30,
    stock_qty: 0, stock_threshold: 0, notes: "",
  }) : ({
    name: "", category: "peinture", unit: "L", unit_price_ht: 0,
    supplier: "", stock_qty: 0, stock_threshold: 0, notes: "",
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (isArt) {
        if (!form.code || !form.description) {
          toast.error("Code et nom sont requis");
          setSaving(false);
          return;
        }
        await api.post("/work-items", {
          code: form.code,
          description: form.description,
          category: form.category,
          unit: form.unit,
          labor_hours_per_unit: 0,
          materials: [],
          tools: [],
          margin_pct: Number(form.margin_pct) || 0,
          notes: form.notes || "",
          kind: "article",
          purchase_price_ht: Number(form.purchase_price_ht) || 0,
          stock_qty: Number(form.stock_qty) || 0,
          stock_threshold: Number(form.stock_threshold) || 0,
          supplier: form.supplier || "",
        });
        toast.success("Article créé");
      } else {
        if (!form.name) {
          toast.error("Le nom est requis");
          setSaving(false);
          return;
        }
        await api.post("/materials", {
          name: form.name,
          category: form.category,
          unit: form.unit,
          unit_price_ht: Number(form.unit_price_ht) || 0,
          supplier: form.supplier || "",
          stock_qty: Number(form.stock_qty) || 0,
          stock_threshold: Number(form.stock_threshold) || 0,
          notes: form.notes || "",
        });
        toast.success("Matériau créé");
      }
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const cats = isArt ? ARTICLE_CATEGORIES : MATERIAL_CATEGORIES;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="stock-creator">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--border)]">
          <div>
            <div className="text-xs font-semibold" style={{ color: "#FFC400" }}>Nouveau {isArt ? "article" : "matériau"}</div>
            <h3 className="font-display text-xl mt-0.5 flex items-center gap-2">
              <TypeBadge type={kind} />
              {isArt ? "Article (achat / revente)" : "Matériau (consommable)"}
            </h3>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {isArt ? (
            <>
              <div>
                <label className="label">Code *</label>
                <input className="input font-mono" value={form.code} onChange={(e) => setField("code", e.target.value.toUpperCase())} placeholder="ART-001" data-testid="creator-code" />
              </div>
              <div>
                <label className="label">Nom / description *</label>
                <input className="input" value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Ex : Détecteur de fumée DAAF" data-testid="creator-description" />
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <label className="label">Nom *</label>
              <input className="input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ex : Peinture acrylique mate murs (10L)" data-testid="creator-name" />
            </div>
          )}

          <div>
            <label className="label">Catégorie</label>
            <select className="input" value={form.category} onChange={(e) => setField("category", e.target.value)} data-testid="creator-category">
              {cats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="label">Unité</label>
            <select className="input" value={form.unit} onChange={(e) => setField("unit", e.target.value)} data-testid="creator-unit">
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          {isArt ? (
            <>
              <div>
                <label className="label">Prix d&apos;achat HT (€)</label>
                <input type="number" step="0.01" className="input text-right" value={form.purchase_price_ht} onChange={(e) => setField("purchase_price_ht", e.target.value)} data-testid="creator-purchase" />
              </div>
              <div>
                <label className="label">Marge (%)</label>
                <input type="number" step="1" className="input text-right" value={form.margin_pct} onChange={(e) => setField("margin_pct", e.target.value)} data-testid="creator-margin" />
                <div className="text-[11px] text-[var(--grey)] mt-1">Laisser vide pour utiliser la marge entreprise.</div>
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <label className="label">Prix d&apos;achat HT à l&apos;unité (€)</label>
              <input type="number" step="0.01" className="input text-right" value={form.unit_price_ht} onChange={(e) => setField("unit_price_ht", e.target.value)} data-testid="creator-unit-price" />
              <div className="text-[11px] text-[var(--grey)] mt-1">Les matériaux sont refacturés au prix d&apos;achat (pas de marge).</div>
            </div>
          )}

          <div>
            <label className="label">Stock initial</label>
            <input type="number" step="1" className="input text-right" value={form.stock_qty} onChange={(e) => setField("stock_qty", e.target.value)} data-testid="creator-stock" />
          </div>
          <div>
            <label className="label">Seuil d&apos;alerte rupture</label>
            <input type="number" step="1" className="input text-right" value={form.stock_threshold} onChange={(e) => setField("stock_threshold", e.target.value)} data-testid="creator-threshold" />
            <div className="text-[11px] text-[var(--grey)] mt-1">0 = pas d&apos;alerte.</div>
          </div>

          <div className="col-span-2">
            <label className="label">Fournisseur principal</label>
            <input className="input" value={form.supplier} onChange={(e) => setField("supplier", e.target.value)} placeholder="Castorama, Point P…" data-testid="creator-supplier" />
          </div>

          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="creator-notes" />
          </div>
        </div>

        <div className="flex justify-start gap-2 mt-6 pt-4 border-t border-[var(--border)]">
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="creator-save">
            <Save size={14} /> {saving ? "..." : "Créer"}
          </button>
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
        </div>
      </div>
    </div>
  );
}

// ── Éditeur (Article ou Matériau) ───────────────────────────────────────
function StockEditor({ item, onClose, onSaved }) {
  const isArt = item.type === "article";
  const [qty, setQty] = useState(Number(item.stock_qty || 0));
  const [threshold, setThreshold] = useState(Number(item.stock_threshold || 0));
  const [supplier, setSupplier] = useState(item.supplier || "");
  const [purchase, setPurchase] = useState(Number(item.purchase_price_ht || 0));
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (isArt) {
        await api.patch(`/work-items/${item.id}`, {
          code: item.code,
          description: item.description,
          category: item.category,
          unit: item.unit,
          labor_hours_per_unit: 0,
          materials: [],
          tools: [],
          margin_pct: item.margin_pct,
          notes: notes,
          kind: "article",
          purchase_price_ht: Number(purchase) || 0,
          stock_qty: Number(qty) || 0,
          stock_threshold: Number(threshold) || 0,
          supplier: supplier || "",
        });
      } else {
        await api.patch(`/materials/${item.id}`, {
          name: item.description,
          category: item.category,
          unit: item.unit,
          unit_price_ht: Number(purchase) || 0,
          supplier: supplier || "",
          stock_qty: Number(qty) || 0,
          stock_threshold: Number(threshold) || 0,
          notes: notes,
        });
      }
      toast.success("Mis à jour");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card max-w-md w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="stock-editor">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--border)]">
          <div>
            <div className="text-xs font-semibold flex items-center gap-2" style={{ color: "#FFC400" }}>
              <TypeBadge type={item.type} /> {item.code && <span>· {item.code}</span>}
            </div>
            <h3 className="font-display text-xl mt-0.5">{item.description}</h3>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Quantité en stock ({item.unit})</label>
            <input type="number" step="1" className="input text-right" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="stock-qty-input" />
          </div>
          <div>
            <label className="label">Seuil d&apos;alerte rupture ({item.unit})</label>
            <input type="number" step="1" className="input text-right" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="stock-threshold-input" />
            <div className="text-xs text-[var(--grey)] mt-1">Une alerte rouge apparaîtra si stock ≤ seuil. Mettre 0 pour désactiver.</div>
          </div>
          <div>
            <label className="label">Prix d&apos;achat HT (€)</label>
            <input type="number" step="0.01" className="input text-right" value={purchase} onChange={(e) => setPurchase(e.target.value)} data-testid="stock-price-input" />
          </div>
          <div>
            <label className="label">Fournisseur principal</label>
            <input className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Ex : Castorama, Point P…" data-testid="stock-supplier-input" />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="stock-notes-input" />
          </div>
        </div>
        <div className="flex justify-start gap-2 mt-6 pt-4 border-t border-[var(--border)]">
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="stock-save">
            <Save size={14} /> {saving ? "..." : "Enregistrer"}
          </button>
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
        </div>
      </div>
    </div>
  );
}
